import { validateQuantity } from '../../../../utils/quantityValidation';

// Helper function to check if a field has a non-empty value
const hasValue = (field) => {
  return field && String(field).trim();
};

export const isBlankSimDataFilled = (data) => {
  const blankSimData = data?.Prepaid?.['Blank Sim'];
  if (!blankSimData) return false;

  return (
    hasValue(blankSimData.store_id) ||
    hasValue(blankSimData.material_code) ||
    hasValue(blankSimData.quantity)
  );
};

export const isMsisdnDataFilled = (data) => {
  const msisdnData = data?.Prepaid?.['MSISDN'];
  if (!msisdnData) return false;

  return (
    hasValue(msisdnData.store_id) ||
    hasValue(msisdnData.quantity)
  );
};

export const isStarterpackDataFilled = (data) => {
  const starterpackData = data?.['Starter Pack'];
  if (!starterpackData) return false;

  return ['RM5', 'RM10', 'RM30', 'Others'].some(denomination => {
    const fields = starterpackData[denomination];
    if (!fields) {
      return false;
    }
    return hasValue(fields.store_id) ||
           hasValue(fields.material_code) ||
           hasValue(fields.quantity);
  });
};

export const isSoftpinDataFilled = (data) => {
  const softpinData = data?.['Softpin'];
  if (!softpinData) return false;

  return ['RM5', 'RM10', 'RM20', 'RM30', 'RM50', 'RM100'].some(denomination => {
    const fields = softpinData[denomination];
    if (!fields) {
      return false;
    }
    return hasValue(fields.material_code) ||
           hasValue(fields.quantity);
  });
};

export const validateForm = (formData) => {
  let isValid = true;
  let hasValidTestData = false;
  const newErrors = {
    general: {
      cr_project_name: '',
      validity_period: '',
      environment: '',
      test_data: ''
    }
  };

  if (!formData.general.cr_project_name?.trim()) {
    newErrors.general.cr_project_name = 'CR/Project Name is required';
    isValid = false;
  }

  if (!formData.general.validity_period) {
    newErrors.general.validity_period = 'Validity Period is required';
    isValid = false;
  }

  if (!formData.general.environment) {
    newErrors.general.environment = 'Environment is required';
    isValid = false;
  }

  // Validate test data sections
  Object.entries(formData).forEach(([section, items]) => {
    if (section !== 'general') {
      Object.entries(items).forEach(([item, fields]) => {
        // Skip empty entries
        const hasAnyValue = hasValue(fields.store_id) ||
                           hasValue(fields.quantity) ||
                           hasValue(fields.material_code);
        if (!hasAnyValue) return;

        // Initialize error objects
        if (!newErrors[section]) newErrors[section] = {};
        if (!newErrors[section][item]) newErrors[section][item] = {};

        // Validate store_id (except for Reload and Softpin)
        if (section !== 'Reload' && section !== 'Softpin') {
          if (!hasValue(fields.store_id)) {
            newErrors[section][item].store_id = 'Store ID is required';
            isValid = false;
          }
        }

        // Validate quantity for all sections
        const quantityStr = hasValue(fields.quantity);
        if (!quantityStr) {
          newErrors[section][item].quantity = 'Quantity is required';
          isValid = false;
        } else {
          const quantityError = validateQuantity(quantityStr, 'prepaid', item);
          if (quantityError) {
            newErrors[section][item].quantity = quantityError;
            isValid = false;
          }
        }

        // Validate material_code (except for MSISDN)
        if (item !== 'MSISDN' && !hasValue(fields.material_code)) {
          newErrors[section][item].material_code = 'Material Code is required';
          isValid = false;
        }

        // Check if this entry constitutes valid test data
        if (section === 'Reload' || section === 'Softpin') {
          if (hasValue(fields.quantity) && hasValue(fields.material_code)) {
            hasValidTestData = true;
          }
        } else {
          if (hasValue(fields.store_id) &&
              hasValue(fields.quantity) &&
              (item === 'MSISDN' || hasValue(fields.material_code))) {
            hasValidTestData = true;
          }
        }
      });
    }
  });

  if (!hasValidTestData) {
    newErrors.general.test_data = 'At least one test data item must be completely filled';
    isValid = false;
  }

  // Calculate total quantity across all test data items
  const calculateTotalQuantity = (formData) => {
    let total = 0;

    // Iterate through all sections except 'general'
    Object.entries(formData).forEach(([section, items]) => {
      if (section !== 'general') {
        Object.entries(items).forEach(([item, fields]) => {
          if (fields && fields.quantity) {
            const quantity = String(fields.quantity).trim();
            if (quantity !== '') {
              const num = Number(quantity);
              // Only add if it's a valid number
              if (!Number.isNaN(num) && Number.isInteger(num) && num > 0) {
                total += num;
              }
            }
          }
        });
      }
    });

    return total;
  };

  return { isValid, errors: newErrors };
};

export const validateFinalData = (formData, newFields, requesterData) => {
  if (!formData.general.cr_project_name || !formData.general.validity_period || !formData.general.environment) {
    return false;
  }

  const hasTestData = Object.entries(formData).some(([section, items]) => {
    if (section !== 'general') {
      return Object.entries(items).some(([item, fields]) => {
        return fields.store_id && fields.quantity;
      });
    }
    return false;
  }) || newFields.some(field => field.store_id && field.quantity);

  if (!hasTestData) {
    return false;
  }

  if (!requesterData.requesterName || !requesterData.requesterEmail) {
    return false;
  }

  return true;
};

export const validateNewField = (field, value, fieldName) => {
  if (fieldName === 'quantity') {
    return validateQuantity(value, 'prepaid', field.item);
  }

  if (fieldName === 'store_id') {
    if (field.section !== 'Reload' && field.section !== 'Softpin') {
      if (!hasValue(value)) {
        return 'This field is required';
      }
    }
  }

  if (fieldName === 'material_code') {
    if (field.item !== 'MSISDN') {
      if (!hasValue(value)) {
        return 'This field is required';
      }
    }
  }

  return '';
};